/**
 * Caller-agnostic file content viewer. THE CONTENT IS THE HERO — no
 * type-icon placeholder, ever; a file that cannot render inline (office
 * docs, unknown mime) falls back to an honest "no preview" state.
 *
 * The viewer NEVER fabricates content and NEVER offers a download: it shows
 * the real bytes the caller resolved, or nothing. Office documents cannot be
 * rendered inline in a browser without a converter, so they show the honest
 * no-preview state rather than an invented stand-in.
 *
 * Purity contract: no firebase, no @mhmo91/rx, no getBlob/createObjectURL/
 * revokeObjectURL. Binds file.previewUrl directly to element src — the web
 * adapter owns all storage resolution and blob-URL lifecycle.
 */
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { reveal } from '@schmancy/directives/reveal'
import '../button/button'
import { when } from 'lit/directives/when.js'
import { repeat } from 'lit/directives/repeat.js'
import '../button/icon-button'
import '../icons/icon'
import '../typography/typography'
import './zoom-stage'
import './csv-stage'
import './xml-stage'
import './xml-thumbnail'

export interface FileViewerItem {
	/** Caller-owned identity returned in preview failure events. */
	id?: string
	/** Display name — shown in the top bar and thumbnails. */
	name: string
	/** URL the viewer renders content from. Bound directly to element src. */
	previewUrl: string
	/**
	 * IANA MIME type. Drives the rendering switch:
	 *   image/*        → <schmancy-zoom-stage>
	 *   application/pdf → <schmancy-zoom-stage> (rendered pages, not <embed>)
	 *   video/*        → <video>
	 *   audio/*        → <audio>
	 *   text/csv       → read-only CSV table (also .csv with absent/generic MIME)
	 *   XML            → source-backed invoice reading or escaped XML source
	 *   anything else  → honest "no preview available" (office docs, unknown)
	 */
	mime: string | undefined
	/** Optional byte size shown in the info panel. */
	size?: number
	/** Optional agent attribution (e.g. "Samwa AI"). */
	agentLabel?: string
}

export interface FileViewerErrorDetail {
	file: Pick<FileViewerItem, 'id' | 'name' | 'mime'>
	error: { code: 'decode-failed'; message: string }
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

@schmancyElement('schmancy-file-viewer')
export class SchmancyFileViewer extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				width: 100%;
				height: 100%;
			}
			@keyframes fv-fade-in {
				from {
					opacity: 0;
					transform: scale(0.99);
				}
				to {
					opacity: 1;
					transform: scale(1);
				}
			}
			@keyframes fv-thumb-pop {
				0% {
					transform: scale(0.9);
				}
				60% {
					transform: scale(1.08);
				}
				100% {
					transform: scale(1.04);
				}
			}
			@keyframes fv-info-in {
				from {
					opacity: 0;
					transform: translateY(-6px) scale(0.97);
				}
				to {
					opacity: 1;
					transform: translateY(0) scale(1);
				}
			}
			@media (prefers-reduced-motion: reduce) {
				.fv-fade {
					animation: none;
				}
				.fv-info {
					animation: none;
				}
			}
			.fv-fade {
				animation: fv-fade-in 180ms ease-out both;
			}
			.fv-info {
				animation: fv-info-in 140ms cubic-bezier(0.2, 0, 0, 1) both;
			}
		`,
	]

	/** Single file or ordered set. */
	@property({ attribute: false }) files: FileViewerItem | FileViewerItem[] = []
	/** Initial active index (for multi-file sets). */
	@property({ type: Number }) index = 0
	/**
	 * Embedded mode — the viewer fills a persistent pane inside a host that
	 * owns the surrounding chrome, rather than being a dismissable overlay that
	 * owns its own. The host states identity and dismissal, so the viewer
	 * renders no top bar and Escape does not fire `close`: the content is the
	 * whole pane. `name` still names the stage, so the artifact keeps its
	 * accessible name, and the zoom pair renders in both modes because the
	 * gesture it substitutes for is the content's own.
	 * Default false gives the overlay its own chrome — close, title, info.
	 */
	@property({ type: Boolean }) embedded = false

	@state() private activeIndex = 0
	@state() private showInfo = false

	override connectedCallback() {
		super.connectedCallback()
		this.toggleAttribute('data-overlay-viewer', !this.embedded)
		this.activeIndex = this.index
		this.addEventListener('keydown', this.handleKeydown)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('keydown', this.handleKeydown)
	}

	private handleKeydown = (e: KeyboardEvent) => {
		// The stage listens inside this element and pans on ArrowLeft/ArrowRight
		// only while it is horizontally overflowing, leaving the key unprevented
		// otherwise so the horizontal axis falls through to the file set. It runs
		// first — it is the deeper target — so this is the other half of that
		// handover: without it, a reader panning a zoomed page pages away from it.
		if (e.defaultPrevented) return
		// Editing a cell search must keep arrow keys for its caret.
		if (
			e.key.startsWith('Arrow') &&
			e
				.composedPath()
				.some(
					target =>
						target instanceof HTMLElement && (target.matches('input, textarea, select') || target.isContentEditable),
				)
		)
			return
		const files = Array.isArray(this.files) ? this.files : [this.files]
		if (e.key === 'ArrowLeft' && this.activeIndex > 0) {
			e.preventDefault()
			this.go(this.activeIndex - 1)
		} else if (e.key === 'ArrowRight' && this.activeIndex < files.length - 1) {
			e.preventDefault()
			this.go(this.activeIndex + 1)
		} else if (e.key === 'Escape') {
			e.preventDefault()
			if (this.showInfo) {
				this.showInfo = false
			} else if (!this.embedded) {
				this.close()
			}
		}
	}

	private go(idx: number) {
		this.activeIndex = idx
		this.showInfo = false
	}

	private close() {
		this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
	}

	override render() {
		const list = Array.isArray(this.files) ? this.files : [this.files]
		const file = list[this.activeIndex] ?? list[0]
		if (!file) return html``

		const hasStrip = list.length > 1
		const { previewUrl } = file
		const declaredMime = file.mime?.split(';')[0].trim().toLowerCase()
		const mime =
			(!declaredMime || declaredMime === 'application/octet-stream') && /\.csv$/i.test(file.name)
				? 'text/csv'
				: (!declaredMime || declaredMime === 'application/octet-stream') && /\.xml$/i.test(file.name)
					? 'application/xml'
					: declaredMime
		// The two mimes that zoom, on ONE element bound `.file=` — never re-keyed
		// per index — because the stage resets its own scale and pan on a `file`
		// change, which is how paging back to a fresh file returns to fit for free.
		const zooms = (mime?.startsWith('image/') ?? false) || mime === 'application/pdf'

		const content =
			mime === 'text/csv' || mime === 'application/csv'
				? html`<schmancy-csv-stage .file=${file}></schmancy-csv-stage>`
				: !zooms && (mime === 'application/xml' || mime === 'text/xml' || mime?.endsWith('+xml'))
					? html`<schmancy-xml-stage .file=${file}></schmancy-xml-stage>`
					: zooms
						? html`<schmancy-zoom-stage class="w-full h-full" .file=${file}></schmancy-zoom-stage>`
						: mime?.startsWith('video/')
							? html`<video class="w-full h-full" src=${previewUrl} controls></video>`
							: mime?.startsWith('audio/')
								? html`
										<div class="w-full h-full flex items-center justify-center p-8">
											<audio class="w-full" src=${previewUrl} controls></audio>
										</div>
									`
								: // No inline preview (office docs, unknown mime). Honest empty
									// state — never a fabricated stand-in, never a download button.
									html`
										<div class="w-full h-full flex flex-col items-center justify-center gap-6 text-surface-onVariant">
											<div
												class="w-20 h-20 rounded-2xl flex items-center justify-center bg-surface-container border border-outline-variant/20"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="36"
													height="36"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="1.5"
													stroke-linecap="round"
													stroke-linejoin="round"
													style="opacity:0.35"
												>
													<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
													<polyline points="14 2 14 8 20 8" />
												</svg>
											</div>
											<div class="flex flex-col items-center gap-1 text-center">
												<schmancy-typography type="label" token="sm" class="opacity-60">
													No preview available for this file type
												</schmancy-typography>
												<schmancy-typography type="label" token="xs" class="opacity-40">
													${mime ?? 'Unknown type'}
												</schmancy-typography>
											</div>
										</div>
									`

		const thumbStrip = html`
			<div class="shrink-0 flex items-center gap-2 px-3 py-2 border-t border-outline-variant/15 overflow-x-auto">
				<schmancy-icon-button
					icon="chevron_left"
					size="xs"
					?disabled=${this.activeIndex === 0}
					@click=${() => this.go(this.activeIndex - 1)}
					aria-label="Previous file"
				></schmancy-icon-button>

				<div class="flex items-center gap-1.5 flex-1 overflow-x-auto min-w-0">
					${repeat(
						list,
						(f, i) => `${f.name}-${i}`,
						(f, i) => {
							const isActive = i === this.activeIndex
							const thumbnailMime = f.mime?.split(';')[0].trim().toLowerCase()
							const isImg = thumbnailMime?.startsWith('image/') ?? false
							const isXml =
								!isImg &&
								(thumbnailMime === 'application/xml' ||
									thumbnailMime === 'text/xml' ||
									thumbnailMime?.endsWith('+xml') ||
									((!thumbnailMime || thumbnailMime === 'application/octet-stream') && /\.xml$/i.test(f.name)))
							return html`
								<button
									type="button"
									@click=${() => this.go(i)}
									aria-label="${f.name}${isActive ? ' (current)' : ''}"
									aria-pressed="${isActive}"
									class=${this.classMap({
										'flex-none flex flex-col items-stretch rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-150 appearance-none p-0': true,
										'border-primary-default': isActive,
										'border-outline-variant/20 opacity-50 hover:opacity-80': !isActive,
									})}
									style="width: 52px; height: 56px; ${isActive ? 'animation: fv-thumb-pop 280ms cubic-bezier(0.34,1.56,0.64,1) both;' : ''}"
								>
									<div class="flex-1 flex items-center justify-center bg-surface-container overflow-hidden">
										${when(
											isImg,
											() => html`
												<img
													src=${f.previewUrl}
													alt=${f.name}
													class="w-full h-full object-cover pointer-events-none select-none"
												/>
											`,
											() =>
												isXml
													? html`<schmancy-xml-thumbnail
															class="h-full w-full"
															.src=${f.previewUrl}
															.label=${f.name}
														></schmancy-xml-thumbnail>`
													: html`
															<schmancy-icon
																class=${this.classMap({
																	'text-base': true,
																	'text-primary-default': isActive,
																	'text-surface-onVariant opacity-40': !isActive,
																})}
																>draft</schmancy-icon
															>
														`,
										)}
									</div>
									<div class="flex-none h-3.5 flex items-center justify-center bg-surface-containerHigh">
										<span
											class="text-[7px] font-mono truncate w-full text-center px-0.5 ${isActive ? 'text-primary-default' : 'text-surface-onVariant opacity-50'}"
											title="${f.name}"
											>${i + 1}</span
										>
									</div>
								</button>
							`
						},
					)}
				</div>

				<schmancy-icon-button
					icon="chevron_right"
					size="xs"
					?disabled=${this.activeIndex === list.length - 1}
					@click=${() => this.go(this.activeIndex + 1)}
					aria-label="Next file"
				></schmancy-icon-button>

				<schmancy-typography
					type="label"
					token="xs"
					class="text-surface-onVariant opacity-50 tabular-nums shrink-0 ml-1"
				>
					${this.activeIndex + 1} / ${list.length}
				</schmancy-typography>
			</div>
		`

		// The overlay's own chrome. An embedded viewer never renders it: the host
		// frames the pane and names the subject, so a strip carrying only info
		// spends 44px above the content on controls the host already sits beside.
		// Zoom is not here at all — it belongs to the stage, which renders its own
		// pair in both modes.
		const topBar = html`
			<div class="flex items-center gap-2 px-2 py-1 pr-20 shrink-0">
				<schmancy-button
					size="xs"
					variant="text"
					aria-expanded=${this.showInfo}
					@click=${() => {
						this.showInfo = !this.showInfo
					}}
					>Details</schmancy-button
				>
				<schmancy-typography type="label" token="xs" class="flex-1 min-w-0 truncate">${file.name}</schmancy-typography>
			</div>
		`

		return html`
			<div class="w-full h-full flex flex-col">
				${when(!this.embedded, () => topBar)}

				<div class="flex flex-1 min-h-0 overflow-hidden">
					<aside
						${reveal(this.showInfo, { axis: 'width', preset: 'snappy' })}
						class="shrink-0 overflow-hidden"
						aria-label="File details"
					>
						<div class="w-56 max-w-full p-3 grid gap-3">
							<schmancy-typography type="label" token="xs">Name</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="break-words">${file.name}</schmancy-typography>
							<schmancy-typography type="label" token="xs">Type</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="break-words">${mime ?? 'Unknown'}</schmancy-typography>
							${when(file.size !== undefined, () => html`<schmancy-typography type="body" token="sm">${formatBytes(file.size!)}</schmancy-typography>`)}
							${when(!!file.agentLabel, () => html`<schmancy-typography type="body" token="sm">${file.agentLabel}</schmancy-typography>`)}
						</div>
					</aside>
					<div class="flex-1 min-w-0 min-h-0 overflow-hidden relative fv-fade">${content}</div>
				</div>

				${when(hasStrip, () => thumbStrip)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-file-viewer': SchmancyFileViewer
	}
}
